import React, { useState, useEffect, useRef, useCallback } from "react";
import { auth, db, storage } from "../firebase-config";
import { 
  collection, 
  doc, 
  addDoc, 
  deleteDoc, 
  onSnapshot, 
  query, 
  orderBy, 
  limit,
  serverTimestamp,
  getDocs
} from "firebase/firestore";
import { 
  ref, 
  uploadBytesResumable, 
  getDownloadURL, 
  deleteObject 
} from "firebase/storage";
import { useToast } from "../context/ToastContext";
import "./EventGallery.scss";

const EventGallery = ({ userId }) => {
  const { addToast } = useToast();
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showCamera, setShowCamera] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const streamRef = useRef(null);

  // Fetch user's gallery photos
  useEffect(() => {
    if (!userId) return;

    const photosQuery = query(
      collection(db, "users", userId, "galleryPhotos"),
      orderBy("createdAt", "desc"),
      limit(4)
    );

    const unsubscribe = onSnapshot(photosQuery, (snapshot) => {
      const photosList = [];
      snapshot.forEach((doc) => {
        photosList.push({ id: doc.id, ...doc.data() });
      });
      setPhotos(photosList);
    });

    return () => unsubscribe();
  }, [userId]);

  // Compress image before upload
  const compressImage = (input, maxWidth = 1200, maxHeight = 1200, quality = 0.8) => {
    return new Promise((resolve, reject) => {
      // If input is already a blob (from camera), convert to File
      if (input instanceof Blob && !(input instanceof File)) {
        const file = new File([input], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
        input = file;
      }
      
      const reader = new FileReader();
      reader.readAsDataURL(input);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let width = img.width;
          let height = img.height;

          // Calculate new dimensions
          if (width > height) {
            if (width > maxWidth) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = (width * maxHeight) / height;
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error("Failed to create blob"));
              }
            },
            "image/jpeg",
            quality
          );
        };
        img.onerror = () => reject(new Error("Failed to load image"));
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
    });
  };

  // Start camera
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: "environment",
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        } 
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
      }
      setShowCamera(true);
    } catch (error) {
      console.error("Error accessing camera:", error);
      addToast("Unable to access camera. Please use file upload instead.");
      // Fallback to file upload
      fileInputRef.current?.click();
    }
  };

  // Stop camera
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setShowCamera(false);
    setCapturedImage(null);
  };

  // Capture photo from camera
  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0);

    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      setCapturedImage({ blob, url });
    }, "image/jpeg", 0.9);
  };

  // Upload photo to Firebase
  const uploadPhoto = async (blob, filename = null) => {
    if (!userId) {
      addToast("Please log in to upload photos.");
      return;
    }
    
    if (photos.length >= 4) {
      addToast("Maximum 4 photos allowed. Please delete an existing photo first.");
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // Compress the image
      console.log("Starting compression...", blob);
      const compressedBlob = await compressImage(blob);
      console.log("Compression successful, blob size:", compressedBlob.size);
      
      // Generate filename
      const timestamp = Date.now();
      const fileName = filename || `gallery_${timestamp}.jpg`;
      
      // Upload to Firebase Storage
      const storageRef = ref(
        storage,
        `muuzbox/users/${userId}/gallery/${fileName}`
      );
      
      console.log("Starting upload to path:", `muuzbox/users/${userId}/gallery/${fileName}`);
      const uploadTask = uploadBytesResumable(storageRef, compressedBlob);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const progress = Math.round(
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          );
          setUploadProgress(progress);
          console.log("Upload progress:", progress);
        },
        (error) => {
          console.error("Firebase upload error:", error);
          console.error("Error code:", error.code);
          console.error("Error message:", error.message);
          
          // Provide more specific error messages
          if (error.code === 'storage/unauthorized') {
            addToast("Permission denied. Please check your authentication.");
          } else if (error.code === 'storage/canceled') {
            addToast("Upload was cancelled.");
          } else if (error.code === 'storage/unknown') {
            addToast("An unknown error occurred. Please try again.");
          } else {
            addToast(`Upload failed: ${error.message}`);
          }
          
          setUploading(false);
          setUploadProgress(0);
        },
        async () => {
          try {
            // Get download URL
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            console.log("Upload complete, URL:", downloadURL);
            
            // Save metadata to Firestore
            await addDoc(collection(db, "users", userId, "galleryPhotos"), {
              url: downloadURL,
              fileName: fileName,
              createdAt: serverTimestamp(),
              storagePath: `muuzbox/users/${userId}/gallery/${fileName}`
            });

            addToast("Photo uploaded successfully!");
            setUploading(false);
            setUploadProgress(0);
            stopCamera();
          } catch (error) {
            console.error("Error saving to Firestore:", error);
            addToast("Photo uploaded but failed to save metadata. Please try again.");
            setUploading(false);
          }
        }
      );
    } catch (error) {
      console.error("Error in uploadPhoto:", error);
      addToast(`Failed to process photo: ${error.message}`);
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Handle file selection
  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      addToast("Please select an image file.");
      return;
    }

    await uploadPhoto(file, file.name);
  };

  // Delete photo
  const deletePhoto = async (photo) => {
    if (!window.confirm("Are you sure you want to delete this photo?")) return;

    try {
      // Delete from Storage
      const storageRef = ref(storage, photo.storagePath);
      await deleteObject(storageRef);
      
      // Delete from Firestore
      await deleteDoc(doc(db, "users", userId, "galleryPhotos", photo.id));
      
      addToast("Photo deleted successfully!");
    } catch (error) {
      console.error("Error deleting photo:", error);
      addToast("Failed to delete photo. Please try again.");
    }
  };

  // Check if photos need to be rotated (delete oldest if at max)
  const checkPhotoLimit = async () => {
    if (photos.length >= 4) {
      const photosQuery = query(
        collection(db, "users", userId, "galleryPhotos"),
        orderBy("createdAt", "asc"),
        limit(1)
      );
      
      const snapshot = await getDocs(photosQuery);
      if (!snapshot.empty) {
        const oldestPhoto = snapshot.docs[0];
        const photoData = oldestPhoto.data();
        
        // Delete oldest photo
        try {
          const storageRef = ref(storage, photoData.storagePath);
          await deleteObject(storageRef);
          await deleteDoc(doc(db, "users", userId, "galleryPhotos", oldestPhoto.id));
        } catch (error) {
          console.error("Error removing oldest photo:", error);
        }
      }
    }
  };

  return (
    <div className="event-gallery">
      <div className="gallery-header">
        <h3 className="gallery-title">My Event Photos</h3>
        <p className="gallery-subtitle">Share your moments (max 4 photos)</p>
      </div>

      {/* Photo Grid */}
      <div className="photo-grid">
        {photos.map((photo) => (
          <div key={photo.id} className="photo-item">
            <img 
              src={photo.url} 
              alt="Gallery" 
              onClick={() => setSelectedPhoto(photo)}
            />
            <button 
              className="delete-btn"
              onClick={() => deletePhoto(photo)}
              aria-label="Delete photo"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14zM10 11v6M14 11v6" 
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        ))}
        
        {/* Add Photo Button */}
        {photos.length < 4 && !showCamera && (
          <div className="add-photo-btn" onClick={startCamera}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
              <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2v11z" 
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="12" cy="13" r="4" stroke="currentColor" strokeWidth="2"/>
            </svg>
            <span>Add Photo</span>
          </div>
        )}
      </div>

      {/* Camera View */}
      {showCamera && (
        <div className="camera-modal">
          <div className="camera-container">
            {!capturedImage ? (
              <>
                <video 
                  ref={videoRef} 
                  autoPlay 
                  playsInline
                  className="camera-video"
                />
                <div className="camera-controls">
                  <button onClick={stopCamera} className="cancel-btn">Cancel</button>
                  <button onClick={capturePhoto} className="capture-btn">
                    <svg width="60" height="60" viewBox="0 0 24 24" fill="white">
                      <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="2" fill="none"/>
                      <circle cx="12" cy="12" r="8" fill="white"/>
                    </svg>
                  </button>
                  <button onClick={() => fileInputRef.current?.click()} className="upload-btn">
                    Upload
                  </button>
                </div>
              </>
            ) : (
              <>
                <img src={capturedImage.url} alt="Captured" className="captured-image" />
                <div className="camera-controls">
                  <button onClick={() => setCapturedImage(null)} className="retake-btn">
                    Retake
                  </button>
                  <button 
                    onClick={() => uploadPhoto(capturedImage.blob)} 
                    className="use-photo-btn"
                    disabled={uploading}
                  >
                    {uploading ? `Uploading ${uploadProgress}%` : "Use Photo"}
                  </button>
                </div>
              </>
            )}
            <canvas ref={canvasRef} style={{ display: "none" }} />
          </div>
        </div>
      )}

      {/* Photo Viewer Modal */}
      {selectedPhoto && (
        <div className="photo-viewer" onClick={() => setSelectedPhoto(null)}>
          <div className="photo-viewer-content">
            <img src={selectedPhoto.url} alt="Full size" />
            <button 
              className="close-viewer"
              onClick={() => setSelectedPhoto(null)}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
        style={{ display: "none" }}
      />

      {/* Upload Progress */}
      {uploading && (
        <div className="upload-progress">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default EventGallery;